import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// Types
import Reducers from "types/Reducers";
// Models
import { IAnswerResult } from 'models/Answer';
import { IBucketResult } from 'models/Bucket'
import IPlayer from "models/Player";
// Async
import { getResult, createResult, sendResult, createAnalyticsInteract, getResultAnswers, getIpInfo, getPlayer } from './playerAsync';

interface IState {
  player: IPlayer | null;
  existingPlayer: IPlayer | null;
  result: any;
  resultUploadedAt: string | null;

  answers: IAnswerResult[];
  buckets: { [key:string]:IBucketResult };

  entryTime: number | null;
  answerTime: number;

  question: {
    index: number;
    ready: boolean;
    start: boolean;
    end: boolean;
    answerPicked: boolean;
    answerRefs: string[];
  };

  ipLocationInfo: any;

  previewPageMode: boolean;
};

const initialState:IState = {
  player: null,
  existingPlayer: null,
  result: null,
  resultUploadedAt: null,

  answers: [],
  buckets: {},

  entryTime: null,
  answerTime: 0,

  question: {
    index: 0,
    ready: false,
    start: false,
    end: false,
    answerPicked: false,
    answerRefs: []
  },

  ipLocationInfo: null,

  previewPageMode: false
};

const slice = createSlice({
  name: Reducers.Player,
  initialState,
  reducers: {
    setAnswers: (state, action:PayloadAction<IAnswerResult[]>) => {
      state.answers = [...state.answers, ...action.payload];
    },
    resetAnswersScore: (state) => {
      state.answers = state.answers.map((answer) => ({ ...answer, score: 0 }));
    },
    setBuckets: (state, action:PayloadAction<{ [key:string]:IBucketResult }>) => {
      const buckets = action.payload;
      // Object
      state.buckets = Object.keys(buckets).reduce((acc:any, cur:string) => {
        const bucket = buckets[cur];
        const stateBucket = state.buckets[cur];
        if ( typeof stateBucket !== 'undefined' ){
          acc[cur] = {...bucket, score: stateBucket.score + bucket.score};
        } else {
          acc[cur] = bucket;
        }
        return acc;
      }, {});
    },

    setEntryTime: (state, action:PayloadAction<number>) => {
      state.entryTime = action.payload;
    },

    setAnswerTime: (state, action:PayloadAction<number>) => {
      state.answerTime = action.payload;
    },
    

    setQuestionIndex: (state, action:PayloadAction<number>) => {
      state.question.index = action.payload;
    },
    setQuestionReady: (state, action:PayloadAction<boolean>) => {
      state.question.ready = action.payload;
    },
    setQuestionStart: (state, action:PayloadAction<boolean>) => {
      state.question.start = action.payload;
    },
    setQuestionEnd: (state, action:PayloadAction<boolean>) => {
      state.question.end = action.payload;
    },
    setQuestionAnswerPicked: (state, action:PayloadAction<boolean>) => {
      state.question.answerPicked = action.payload;
    },
    setQuestionAnswerRefs: (state, action:PayloadAction<string[]>) => {
      state.question.answerRefs = action.payload;
    },

    resetQuestionStatuses: (state) => {
      state.question.ready = initialState.question.ready;
      state.question.start = initialState.question.start;
      state.question.end = initialState.question.end;
      state.question.answerPicked = initialState.question.answerPicked;
      state.question.answerRefs = initialState.question.answerRefs;
    },

    setPreviewPageMode: (state, action:PayloadAction<boolean>) => {
      state.previewPageMode = action.payload;
    },

    setExistingPlayer: (state, action:PayloadAction<IPlayer>) => {
      state.existingPlayer = action.payload;
    },

    setInitialState: (state) => {
      state = initialState;
    }
  },
  extraReducers(builder) {
    // Get player
    builder.addCase(getPlayer.pending, (state) => {
      state.player = null;
    });
    builder.addCase(getPlayer.fulfilled, (state, action:PayloadAction<IPlayer>) => {
      state.player = action.payload;
    });
    // Get result
    builder.addCase(getResult.pending, (state) => {
      // state.result = null;
    });
    builder.addCase(getResult.fulfilled, (state, action:PayloadAction<any>) => {
      state.result = action.payload;
      state.resultUploadedAt = new Date().toISOString();
    });
    // Get answers results
    builder.addCase(getResultAnswers.fulfilled, (state, action:PayloadAction<any>) => {
      state.result = action.payload;
    });
    // Create result
    builder.addCase(createResult.pending, (state) => {
    });
    builder.addCase(createResult.fulfilled, (state, action:PayloadAction<any>) => {
    });
    // Send result
    builder.addCase(sendResult.pending, (state) => {
    });
    builder.addCase(sendResult.fulfilled, (state, action:PayloadAction<any>) => {
    });
    // Increase score
    // builder.addCase(increaseScore.pending, (state) => {

    // })
    // builder.addCase(increaseScore.fulfilled, (state, action:PayloadAction<any>) => {
    //   const { resultId, numberOfPoints } = action.payload;
    //   const score = state.result.currentUserResult.score + numberOfPoints;
    //   state.result.currentUserResult.score = score;
    //   if ( state.result.results ){
    //     state.result.results.map((result:any) => {
    //       if ( result._id === resultId ) return {...result, score};
    //       return result;
    //     })
    //   }
    // })
    builder.addCase(createAnalyticsInteract.fulfilled, (state, action:PayloadAction<any>) => {
      const { resultId, numberOfBonusPoints, newRank } = action.payload;
      if ( state.result && state.result.currentUserResult ){
        const score = state.result.currentUserResult.score + numberOfBonusPoints;
        state.result.currentUserResult.score = score;
        if (newRank) {
          state.result.currentUserResult.position = newRank;
        }
        if ( state.result.results ){
          state.result.results = state.result.results.map((result:any) => {
            if ( result._id === resultId ) return {...result, score};
            return result;
          })
        }
      }
    })
    // Get IP info
    builder.addCase(getIpInfo.fulfilled, (state, action:PayloadAction<any>) => {
      state.ipLocationInfo = action.payload;
    });
  },
});

export const {
  setAnswers, setBuckets,
  setEntryTime, setAnswerTime,
  setQuestionIndex, setQuestionReady, setQuestionStart, setQuestionEnd,
  setQuestionAnswerPicked, setQuestionAnswerRefs,
  resetQuestionStatuses, setPreviewPageMode,
  setExistingPlayer,
  resetAnswersScore,
  setInitialState
} = slice.actions;

export default slice.reducer;
